/**
 * Cliente HTTP do laudo grafoscópico (EW.F4.T07 + E8.F6.T14). // ux-neutrality:allow doc-comment
 *
 * Espelha apps/api/runtime/laudo.py: // ux-neutrality:allow doc-comment
 * - GET /casos/:id/laudo retorna HTML auto-contido (CSS embutido). // ux-neutrality:allow doc-comment
 * - GET /casos/:id/laudo.pdf retorna binário application/pdf. // ux-neutrality:allow doc-comment
 * - GET /casos/:id/laudo.docx retorna binário DOCX (E8.F6.T13). // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import { codigoDeVerificacao, type DocumentoBaixado } from '@/lib/http_download';

import type { CaseRecordData } from './cases';

export type LaudoErrorReason =
  | 'case_not_found'
  | 'not_ready'
  | 'narrative_not_ready'
  | 'narrative_not_approved'
  | 'case_review_not_approved'
  | 'analysis_not_in_case'
  | 'render_failed'
  | 'network'
  | 'assistant_timeout'
  | 'narrative_not_found'
  | 'narrative_already_approved'
  | 'edicao_desatualizada'
  | 'narrativa_desatualizada'
  | 'narrativa_em_geracao'
  | 'invalid_edit_value'
  | 'owner_required'
  | 'no_api_key'
  | 'assistant_failed'
  | 'unknown';

// EM.F3.T01: a geração da narrativa chama o Agente (~24s); o backend dá 50s.
// O default de 15s (api.ts) abortava e mostrava erro de chave falso. 60s = 50s
// do backend + margem. Demais chamadas do laudo (GET/PATCH/PDF) seguem o default.
export const LAUDO_NARRATIVE_TIMEOUT_MS = 60_000;

export type VarianteConclusao =
  | 'compatibilidade'
  | 'divergencia'
  | 'incompatibilidade'
  | 'inconclusivo';

export interface NarrativeRecordData {
  id: string;
  case_analysis_id: string;
  estrutura_geral: string;
  morfologia: string;
  conectividade: string;
  inclinacao: string;
  sequencia: string;
  convergencias: string[];
  divergencias: string[];
  avaliacao_tecnica: string;
  sintese: string;
  variante: VarianteConclusao;
  observacao_adicional: string | null;
  model_provider: string;
  model_name: string | null;
  approved_at: string | null;
  approved_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  // E137.F2.C04 (GUS-160): o hash do texto; a aprovação o devolve para provar qual texto foi revisto.
  texto_sha256: string;
}

export type NarrativeEditableFields = Partial<
  Pick<
    NarrativeRecordData,
    | 'estrutura_geral'
    | 'morfologia'
    | 'conectividade'
    | 'inclinacao'
    | 'sequencia'
    | 'convergencias'
    | 'divergencias'
    | 'avaliacao_tecnica'
    | 'sintese'
    | 'observacao_adicional'
  >
>;

export class LaudoError extends Error {
  public readonly reason: LaudoErrorReason;

  constructor(reason: LaudoErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'LaudoError';
  }
}

const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;
const HTTP_INTERNAL = 500;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;
const HTTP_BAD_GATEWAY = 502;
const HTTP_SERVICE_UNAVAILABLE = 503;
const HTTP_GATEWAY_TIMEOUT = 504;

function isClientTimeout(err: AxiosError): boolean {
  // EM.F3.T01: abort por timeout do cliente = "Agente demorou", não falha de rede.
  return err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
}

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  return typeof data?.detail === 'string' ? data.detail : '';
}

// Mapeamento (status, detail) → LaudoErrorReason. Mantém classifyLaudoError
// simples e adiciona novos códigos sem inchar a complexidade ciclomática.
const _STATUS_DETAIL_MAP: ReadonlyArray<{
  httpStatus: number;
  detail: string;
  reason: LaudoErrorReason;
}> = [
  { httpStatus: HTTP_NOT_FOUND, detail: 'CASE_NOT_FOUND', reason: 'case_not_found' },
  { httpStatus: HTTP_NOT_FOUND, detail: 'NARRATIVE_NOT_FOUND', reason: 'narrative_not_found' },
  { httpStatus: HTTP_NOT_FOUND, detail: 'ANALYSIS_NOT_IN_CASE', reason: 'analysis_not_in_case' },
  { httpStatus: HTTP_UNPROCESSABLE, detail: 'CASE_LAUDO_NOT_READY', reason: 'not_ready' },
  { httpStatus: HTTP_UNPROCESSABLE, detail: 'NARRATIVE_NOT_READY', reason: 'narrative_not_ready' },
  {
    httpStatus: HTTP_UNPROCESSABLE,
    detail: 'NARRATIVE_NOT_APPROVED',
    reason: 'narrative_not_approved',
  },
  {
    httpStatus: HTTP_UNPROCESSABLE,
    detail: 'CASE_REVIEW_NOT_APPROVED',
    reason: 'case_review_not_approved',
  },
  { httpStatus: HTTP_INTERNAL, detail: 'LAUDO_RENDER_FAILED', reason: 'render_failed' },
  { httpStatus: HTTP_FORBIDDEN, detail: 'OWNER_REQUIRED', reason: 'owner_required' },
  { httpStatus: HTTP_CONFLICT, detail: 'USER_HAS_NO_API_KEY', reason: 'no_api_key' },
  {
    httpStatus: HTTP_CONFLICT,
    detail: 'NARRATIVE_ALREADY_APPROVED',
    reason: 'narrative_already_approved',
  },
  { httpStatus: HTTP_UNPROCESSABLE, detail: 'INVALID_EDIT_VALUE', reason: 'invalid_edit_value' },
  // E137.F2 (GUS-160): o campo mudou depois que o editor o leu; a edição antiga não sobrescreve.
  {
    httpStatus: HTTP_CONFLICT,
    detail: 'LAUDO_EDICAO_DESATUALIZADA',
    reason: 'edicao_desatualizada',
  },
  // E137.F2.C04 (GUS-160): o texto mudou desde a revisão; ou está sendo gerado agora.
  {
    httpStatus: HTTP_CONFLICT,
    detail: 'NARRATIVA_DESATUALIZADA',
    reason: 'narrativa_desatualizada',
  },
  { httpStatus: HTTP_CONFLICT, detail: 'NARRATIVA_EM_GERACAO', reason: 'narrativa_em_geracao' },
];

// 502/503 = Agente indisponível/falhou. 504 (Gateway Timeout) é tratado à
// parte como timeout, espelhando o chat (EM.F1) e a semântica de HTTP: o
// gateway desistiu da espera pelo Agente — para o perito é "demorou", não
// "falhou". O abort ECONNABORTED do cliente (isClientTimeout) tem o mesmo fim.
const _ASSISTANT_FAILURE_STATUSES: ReadonlySet<number> = new Set([
  HTTP_BAD_GATEWAY,
  HTTP_SERVICE_UNAVAILABLE,
]);

export function classifyLaudoError(err: unknown): LaudoError {
  if (!axios.isAxiosError(err)) return new LaudoError('unknown');
  const axiosErr = err as AxiosError;
  const httpStatus = axiosErr.response?.status ?? 0;
  const detail = detailString(axiosErr);
  const match = _STATUS_DETAIL_MAP.find((m) => m.httpStatus === httpStatus && m.detail === detail);
  if (match) return new LaudoError(match.reason);
  if (httpStatus === HTTP_GATEWAY_TIMEOUT) return new LaudoError('assistant_timeout');
  if (_ASSISTANT_FAILURE_STATUSES.has(httpStatus)) return new LaudoError('assistant_failed');
  if (isClientTimeout(axiosErr)) return new LaudoError('assistant_timeout');
  if (isNetworkError(axiosErr)) return new LaudoError('network');
  return new LaudoError('unknown');
}

// Com `responseType: 'blob'`, axios entrega o CORPO de erro (422/404 com
// `{detail}`) como Blob, não como objeto — `classifyLaudoError` então lê
// `detail` vazio e cai em 'unknown'. No preview, distinguir
// `narrative_not_ready` (rascunho sem narrativa → estado vazio, não erro) de
// falha real é essencial; relemos o Blob como texto e reidratamos `response.data`.
export async function rehydrateBlobError(err: unknown): Promise<unknown> {
  if (!axios.isAxiosError(err)) return err;
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      (err.response as { data: unknown }).data = parsed;
    } catch {
      // Corpo não-JSON (ex.: binário corrompido) — mantém como veio.
    }
  }
  return err;
}

/**
 * Baixa o PDF do laudo com o código de verificação do arquivo (E85.F2).
 *
 * O código vem do cabeçalho calculado pelo servidor sobre os MESMOS bytes da
 * resposta — é o que a pessoa confere com `sha256sum` no arquivo salvo.
 */
export async function getLaudoPdfBlob(
  client: AxiosInstance,
  caseId: string,
): Promise<DocumentoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/laudo.pdf`, {
      responseType: 'blob',
    });
    return { blob: resposta.data, codigoVerificacao: codigoDeVerificacao(resposta.headers) };
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

/**
 * Busca o PDF de PRÉ-VISUALIZAÇÃO do laudo (E23.F20 — ADR-0030). // ux-neutrality:allow doc-comment
 *
 * Distinto de `getLaudoPdfBlob`: chama `GET /casos/:id/laudo/preview.pdf`, que
 * tolera rascunho (renderiza sobre o texto atual mesmo sem aprovação). Ainda
 * pode levantar `narrative_not_ready` se a contestada não tem narrativa.
 */
export async function getLaudoPreviewPdfBlob(client: AxiosInstance, caseId: string): Promise<Blob> {
  try {
    const { data } = await client.get<Blob>(`/casos/${caseId}/laudo/preview.pdf`, {
      responseType: 'blob',
    });
    return data;
  } catch (err) {
    throw classifyLaudoError(await rehydrateBlobError(err));
  }
}

export async function getLaudoDocxBlob(
  client: AxiosInstance,
  caseId: string,
): Promise<DocumentoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/laudo.docx`, {
      responseType: 'blob',
    });
    return { blob: resposta.data, codigoVerificacao: codigoDeVerificacao(resposta.headers) };
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

// E35.F1.T04: caracteres inválidos em nome de arquivo (separadores de path e os
// reservados pelo Windows/POSIX). Espelha `_FILENAME_INVALID` do backend
// (apps/api/services/laudo_service.py) — o número da pasta usa "/".
// eslint-disable-next-line no-control-regex
const FILENAME_INVALID = /[\\/:*?"<>|\x00-\x1f]+/g;

/**
 * Nome-base do arquivo de laudo baixado, sem extensão (E35.F1.T04).
 *
 * Espelha `laudo_download_basename` do backend: "{pasta} - ctt {contrato}",
 * onde pasta = título do caso (número interno do escritório). Sem contrato,
 * fica só a pasta; sem título, recai no código gerado.
 */
export function laudoDownloadBasename(caseRecord: CaseRecordData): string {
  const pasta = (caseRecord.title || caseRecord.display_code).trim();
  const contrato = (caseRecord.contrato ?? '').trim();
  const base = contrato ? `${pasta} - ctt ${contrato}` : pasta;
  const sanitized = base.replace(FILENAME_INVALID, '-').trim();
  return sanitized || caseRecord.display_code;
}

export function triggerPdfDownload(blob: Blob, basename: string): void {
  triggerBlobDownload(blob, `${basename}.pdf`);
}

export function triggerDocxDownload(blob: Blob, basename: string): void {
  triggerBlobDownload(blob, `${basename}.docx`);
}

// ---------------------------------------------------------------------------
// Narrativa do laudo (E9.F1.T07) — POST/GET/PATCH /casos/:id/laudo/narrative
// ---------------------------------------------------------------------------

export async function generateNarrative(
  client: AxiosInstance,
  caseId: string,
  caseAnalysisId: string,
): Promise<NarrativeRecordData> {
  try {
    const { data } = await client.post<NarrativeRecordData>(
      `/casos/${caseId}/laudo/narrative`,
      { case_analysis_id: caseAnalysisId },
      { timeout: LAUDO_NARRATIVE_TIMEOUT_MS },
    );
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

export async function getNarrative(
  client: AxiosInstance,
  caseId: string,
  caseAnalysisId: string,
): Promise<NarrativeRecordData | null> {
  try {
    const { data } = await client.get<NarrativeRecordData>(`/casos/${caseId}/laudo/narrative`, {
      params: { case_analysis_id: caseAnalysisId },
    });
    return data;
  } catch (err) {
    const classified = classifyLaudoError(err);
    if (classified.reason === 'narrative_not_found') return null;
    throw classified;
  }
}

export async function patchNarrative(
  client: AxiosInstance,
  caseId: string,
  caseAnalysisId: string,
  fields: NarrativeEditableFields,
): Promise<NarrativeRecordData> {
  try {
    const { data } = await client.patch<NarrativeRecordData>(`/casos/${caseId}/laudo/narrative`, {
      case_analysis_id: caseAnalysisId,
      ...fields,
    });
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

// E137.F2.C04 (GUS-160): aprova o texto revisado — o servidor só aprova se o texto gravado ainda
// tem esse hash (409 NARRATIVA_DESATUALIZADA) e não está sendo gerado (409 NARRATIVA_EM_GERACAO).
export async function approveNarrative(
  client: AxiosInstance,
  caseId: string,
  caseAnalysisId: string,
  textoSha256: string,
): Promise<NarrativeRecordData> {
  try {
    const { data } = await client.post<NarrativeRecordData>(
      `/casos/${caseId}/laudo/narrative/${caseAnalysisId}/approve`,
      { texto_sha256: textoSha256 },
    );
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

export async function unapproveNarrative(
  client: AxiosInstance,
  caseId: string,
  caseAnalysisId: string,
): Promise<NarrativeRecordData> {
  try {
    const { data } = await client.delete<NarrativeRecordData>(
      `/casos/${caseId}/laudo/narrative/${caseAnalysisId}/approve`,
    );
    return data;
  } catch (err) {
    throw classifyLaudoError(err);
  }
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
